<template>
    <div ref="contextMenu"
         v-if="menuVisible"
         v-bind:style="menuStyle"
         v-on:blur="closeMenu"
         class="fm-context-menu"
         tabindex="-1">
        <ul class="list-unstyled">
            <li v-if="isFile" class="context-download" v-on:click="menuAction('download')">
                <i class="fa-fw fas fa-download"></i><span>Download</span>
            </li>
            <li v-if="isFile" class="context-properties">
                <component v-bind:is="propertyComponentName"></component>
            </li>
            <li v-if="isFile" class="context-delete" v-on:click="copyFileLink()">
                <i class="fa-fw fas fa-link text-danger"></i> <span>Copy Link</span>
            </li>
            <li v-else class="context-delete" v-on:click="copyFolderLink()">
                <i class="fa-fw fas fa-link text-danger"></i> <span>Copy Link</span>
            </li>
            <li class="context-delete" v-if="!isLocked" v-on:click="deleteItems()">
                <i class="fa-fw far fa-trash-alt text-danger"></i> <span v-if="isFile">Delete File</span><span v-else>Delete Folder</span>
            </li>
        </ul>
    </div>
</template>

<script>
/* eslint-disable no-param-reassign */
import EventBus from './../../eventBus';
import translate from '../../mixins/translate';
import contextMenu from './mixins/contextMenu';
import contextMenuRules from './mixins/contextMenuRules';
import contextMenuActions from './mixins/contextMenuActions';

import Properties from '../modals/views/Properties.vue';

export default {
  name: 'ContextMenu',
  components: { Properties },
  mixins: [translate, contextMenu, contextMenuRules, contextMenuActions],
  data() {
    return {
      propertyComponentName: 'properties',
      menuVisible: false,
      menuStyle: {
        top: 0,
        left: 0,
      },
    };
  },
  mounted() {
    /**
             * Listen events
             * 'contextMenu'
             */
    EventBus.$on('contextMenu', event => this.showMenu(event));
  },
  computed: {
    isFile() {
      return this.$store.getters['fm/selectedItems'].filter(item => item.type && item.type != 'dir').length > 0;
    },
    isLocked() {
      return this.$store.getters['fm/selectedItems'].filter(item => item.locked).length > 0;
    },
    /**
             * Context menu items
             * @returns {*}
             */
    menu() {
      return this.$store.state.fm.settings.contextMenu;
    },
  },
  methods: {
    copyFileLink() {
      const folder = this.getPath();
      const file = this.getName();

      const url = `${window.location.origin + window.location.pathname}?folder=${folder}&file=${file}`;

      // Now I need to copy the path to the user's clipboard.
      navigator.clipboard.writeText(url).then(() => {
        this.$store.commit('updateMessage', {
          message: `Copied ${url} to clipboard`,
          snackbar: true,
        });
        console.log(`Copied ${url} to clipboard`);
      });
    },
    getName() {
      const item = this.$store.getters['fm/selectedItems'][0];
      return item.basename;
    },
    getPath() {
      const item = this.$store.getters['fm/selectedItems'][0];
      const path = item.type === 'file' ? item.dirname : item.path;

      const split = path.split('/');
      if (split[0] === 'file_manager') {
        return path.split('/').slice(3).join('/');
      }

      // TODO: 20240909: I would love to have this passed in as a prop or otherwise
      // set someplace else and then pulled in (i.e. via store?).
      return 'Group Wall Files';
    },
    copyFolderLink() {
      let path = getPath();
      // The path uses / as the separator.
      // I need to remove the first 3 segments to get the right path.
      path = path.split('/').slice(3).join('/');
      const url = `${window.location.origin + window.location.pathname}?folder=${path}`;

      // Now I need to copy the path to the user's clipboard.
      navigator.clipboard.writeText(url).then(() => {
        this.$store.commit('updateMessage', {
          message: `Copied ${url} to clipboard`,
          snackbar: true,
        });
        console.log(`Copied ${url} to clipboard`);
      });
    },
    deleteItems() {
      // create items list for delete
      const items = this.$store.getters['fm/selectedItems'].map(item => ({
        path: item.path,
        type: item.type,
      }));

      if (confirm('Are you sure you want to delete this file?')) {
        // create items list for delete
        this.$store.dispatch('fm/delete', items).then(() => {
          // Delete successful.
          this.menuVisible = false;
        });
      }
    },
    /**
             * Should this group be visible?
             *
             * @param group
             */
    isGroupVisible(group) {
      // Check if there are any items that can be shown.
      for (const item of group) {
        if (item && item.hasOwnProperty('name') && this.showMenuItem(item.name)) {
          return true;
        }
      }

      return false;
    },
    /**
             * Show context menu
             * @param event
             */
    showMenu(event) {
      if (this.selectedItems) {
        this.menuVisible = true;

        // focus on menu
        this.$nextTick(() => {
          if (this.$refs.contextMenu) {
            this.setMenu(event.pageY, event.pageX);

            this.$nextTick(() => {
              // 2019-11-07: Setting focus causes issues in long grids.
              // The page will jump (away from grid item).
              // So I put a tick inside a tick and now all is well.
              this.$refs.contextMenu.focus();
            });
          }
        });
      }
    },

    /**
             * Set context menu coordinates
             * @param top
             * @param left
             */
    setMenu(top, left) {
      if (!this.$refs.contextMenu) {
        return;
      }

      // get parent el (.fm-body)
      const el = this.$refs.contextMenu.parentNode;

      // get parent el size
      const elSize = el.getBoundingClientRect();

      // actual coordinates of the block
      const elY = window.pageYOffset + elSize.top;
      const elX = window.pageXOffset + elSize.left;

      // calculate the preliminary coordinates
      let menuY = top - elY;
      let menuX = left - elX;

      // calculate max X and Y coordinates
      const maxY = elY + (el.offsetHeight - this.$refs.contextMenu.offsetHeight - 25);
      const maxX = elX + (el.offsetWidth - this.$refs.contextMenu.offsetWidth - 25);

      if (top > maxY) menuY = maxY - elY;
      if (left > maxX) menuX = maxX - elX;

      // set coordinates
      this.menuStyle.top = `${menuY}px`;
      this.menuStyle.left = `${menuX}px`;
    },

    /**
             * Close context menu
             */
    closeMenu() {
      this.menuVisible = false;
    },

    /**
             * Show context menu item
             * @param name
             * @returns {*}
             */
    showMenuItem(name) {
      if (Object.prototype.hasOwnProperty.call(this, `${name}Rule`)) {
        return this[`${name}Rule`]();
      }

      return false;
    },

    /**
             * Call actions when clicking the context menu
             * @param name
             */
    menuAction(name) {
      if (Object.prototype.hasOwnProperty.call(this, `${name}Action`)) {
        this[`${name}Action`]();
      }
      // close context menu
      this.closeMenu();
    },
  },
};
</script>

<style lang="scss">
    .fm-context-menu {
        position: absolute;
        z-index: 9997;
        background-color: white;
        box-shadow: 3px 2px 5px gray;
        border-radius: 5px;

    .list-unstyled {
        margin-bottom: 0;
        border-bottom: 1px solid rgba(0,0,0,.125);
    }

    ul > li {
        padding: 0.4rem 1rem;
    }

    ul > li:not(.disabled) {
        cursor: pointer;

    &:hover {
         background-color: #f8f9fa;
     }

    i {
        padding-right: 2rem;
    }
    }
    }
</style>
